import { apiCall, isBackendConfigured } from './api-call'
import { ApiResponse } from '../_types/api'
import {
  ClaimFlowFormVerificationResult,
  FlowFormVerificationAttempt,
  GrantFlowFormVerificationResult,
  VerificationConnectRequest,
} from '../_types/flow-form-verification'

/**
 * Reads why the server refused, in the words worth showing somebody.
 *
 * `getErrorReturnObject` puts the friendly wording in `data` and the raw error
 * in `message`, so `data` is preferred when it is a string. Some refusals carry
 * an object there instead, and those fall back to `message`, which on the guard
 * paths is already a readable sentence.
 *
 * @param {ApiResponse} response - the refusal
 * @returns {string} the reason to show
 */
const readRefusalReason = (response: ApiResponse): string => {
  if (typeof response.data === 'string' && response.data.trim() !== '') {
    return response.data
  }

  if (typeof response.message === 'string' && response.message.trim() !== '') {
    return response.message
  }

  return 'QuickPass would not share that just now.'
}

/**
 * Shares the renter's existing verification with a form step they are filling
 * in elsewhere.
 *
 * Runs inside QuickPass under the renter's own session, which is what makes it
 * safe: the only verification that can be shared is one the signed-in renter
 * actually owns. `wasShared: false` is an ordinary answer meaning they have no
 * current check of that kind, not a failure.
 *
 * A refusal comes back with its reason rather than as a bare null. Being told
 * the step is switched off, or that this account does not own that pass, is
 * something a person can act on; "we could not reach QuickPass" when QuickPass
 * answered immediately is not.
 *
 * @param {string} quickPassUserId - the signed-in renter
 * @param {VerificationConnectRequest} request - the form step being approved
 * @returns {Promise<FlowFormVerificationAttempt<GrantFlowFormVerificationResult>>}
 */
const grantFlowFormVerification = async (
  quickPassUserId: string,
  request: VerificationConnectRequest,
): Promise<FlowFormVerificationAttempt<GrantFlowFormVerificationResult>> => {
  if (isBackendConfigured() === false) {
    return { result: null, reason: 'QuickPass is not configured here.' }
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'grantflowformverification',
      data: {
        quickPassUserId,
        companyId: request.companyId,
        formId: request.formId,
        nodeId: request.nodeId,
        submissionId: request.submissionId,
        verificationRequestToken: request.verificationRequestToken,
      },
    })

    if (response.code !== 200) {
      console.warn('[flow-form-verification] grant refused', response)
      return { result: null, reason: readRefusalReason(response) }
    }

    return {
      result: JSON.parse(response.data) as GrantFlowFormVerificationResult,
      reason: '',
    }
  } catch (error) {
    console.warn('[flow-form-verification] grant failed', error)
    return {
      result: null,
      reason: 'We could not reach QuickPass just now.',
    }
  }
}

/**
 * Keeps a check the renter just passed on a form, so their next form can reuse
 * it instead of charging somebody to run it again.
 *
 * @param {string} quickPassUserId - the signed-in renter
 * @param {VerificationConnectRequest} request - the form step whose result is being kept
 * @returns {Promise<ClaimFlowFormVerificationResult | null>}
 */
const claimFlowFormVerification = async (
  quickPassUserId: string,
  request: VerificationConnectRequest,
): Promise<ClaimFlowFormVerificationResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'claimflowformverification',
      data: {
        quickPassUserId,
        submissionId: request.submissionId,
        nodeId: request.nodeId,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ClaimFlowFormVerificationResult
  } catch (error) {
    console.warn('[flow-form-verification] claim failed', error)
    return null
  }
}

export { grantFlowFormVerification, claimFlowFormVerification }
